/**
 * Viewport fix utility for OAuth redirect issues on mobile Chrome
 * 
 * Problem: Some mobile browsers (especially Chrome on Android) switch to 
 * "Desktop site" mode after OAuth redirects through external domains like
 * accounts.google.com. This causes the app to render with desktop viewport
 * instead of mobile.
 * 
 * Solution: Detect when viewport is wider than physical screen and force
 * a viewport meta tag reset to restore mobile layout.
 */

/**
 * Detects if browser is in forced desktop mode on a mobile device
 * @returns true if mobile device is showing desktop viewport
 */
export function isDesktopModeForced(): boolean {
  // Check if user agent indicates mobile device
  const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
  
  if (!isMobileUA) return false;
  
  // On mobile devices, innerWidth should not exceed screen.width
  // If it does, browser is likely in "Desktop site" mode
  const screenWidth = window.screen.width;
  const viewportWidth = window.innerWidth;
  
  // Allow small tolerance for browser chrome differences
  return viewportWidth > screenWidth * 1.1;
}

/**
 * Forces a viewport meta tag reset to restore mobile layout
 * Uses double requestAnimationFrame for reliable browser repaint
 */
export function forceViewportReset(): void {
  const viewport = document.querySelector('meta[name="viewport"]');
  if (!viewport) return;
  
  const originalContent = viewport.getAttribute('content');
  
  // Step 1: Force strict mobile viewport with no scaling
  viewport.setAttribute(
    'content',
    'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no'
  );
  
  // Step 2: After browser processes the change, restore original settings
  // Using double rAF ensures the browser has time to recalculate layout
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      viewport.setAttribute(
        'content',
        originalContent || 'width=device-width, initial-scale=1, viewport-fit=cover'
      );
    });
  });
}

/**
 * Checks for desktop mode and applies fix if needed
 * Safe to call multiple times - only acts when necessary
 */
export function applyViewportFixIfNeeded(): void {
  if (isDesktopModeForced()) {
    console.warn('[Viewport] Desktop mode detected on mobile device, applying fix');
    forceViewportReset();
  }
}
